// The full system test report (docs/clairvoyance/17-testing-and-simulation.md §17.2).
// Written by the server to `systemTestRuns/{runId}`; read by the office Dev tools page and the CLI.
import { z } from 'zod';

export const SYSTEM_TEST_VARIANTS = [
  'metro_manila',
  'provincial_hauler',
  'prime',
  'unpaid_blocked',
  'customs_delay',
  'offline_driver',
  'missing_found',
  'reserve_shortfall',
  'partner_quiet',
  'cancel_after_payment',
] as const;
export type SystemTestVariant = (typeof SYSTEM_TEST_VARIANTS)[number];

export const SYSTEM_TEST_VARIANT_LABELS: Record<SystemTestVariant, string> = {
  metro_manila: 'Metro Manila, own delivery',
  provincial_hauler: 'Province, delivered by a hauler',
  prime: 'Prime courier',
  unpaid_blocked: 'Unpaid box is blocked',
  customs_delay: 'Customs delay raises alerts',
  offline_driver: 'Driver works offline',
  missing_found: 'A box goes missing at devanning and is found',
  reserve_shortfall: 'Reserves fall short of the port costs',
  partner_quiet: 'A hauler goes quiet: chase, then escalate',
  cancel_after_payment: 'A paid shipment is amended, then cancelled with a refund',
};

export const startSystemTestSchema = z
  .object({ variant: z.enum(SYSTEM_TEST_VARIANTS).default('metro_manila') })
  .strict();

/** The control tower jobs Dev tools can run on demand, at the real time or a chosen one (17 §17.2). */
export const CONTROL_TOWER_JOBS = [
  'watch',
  'cashCalendar',
  'digest',
  'scorecards',
  'snapshot',
  'feedbackScores',
  // Phase 7 (15 §15.1, 13 §13.3): the dashboards and season boards, leg times for the latest
  // deliveries, and the BigQuery export (to the fake writer on local).
  'dashboards',
  'legs',
  'bigquery',
] as const;
export const runControlTowerInputSchema = z
  .object({
    now: z.iso.datetime({ offset: true }).optional(),
    jobs: z.array(z.enum(CONTROL_TOWER_JOBS)).min(1).default(['watch', 'cashCalendar']),
  })
  .strict();
export type RunControlTowerInput = z.input<typeof runControlTowerInputSchema>;

export const STEP_STATUSES = [
  'pending',
  'running',
  'passed',
  'warning',
  'failed',
  'skipped',
  'not_built',
] as const;
export type StepStatus = (typeof STEP_STATUSES)[number];

export interface StepCheck {
  label: string;
  ok: boolean;
  /** A warning check does not fail the step. */
  warnOnly?: boolean;
  expected?: string;
  actual?: string;
}

export interface StepLink {
  label: string;
  /** Firestore document path, for example `commands/cmd_…`. */
  path: string;
}

export interface StepReport {
  id: string;
  title: string;
  /** The roadmap phase that builds this step (10-roadmap.md). */
  phase: number;
  status: StepStatus;
  durationMs?: number;
  checks: StepCheck[];
  /** Why the step failed or was skipped, in plain words. */
  note?: string;
  links: StepLink[];
}

export type RunStatus = 'queued' | 'running' | 'passed' | 'failed';

export interface RunSummary {
  passed: number;
  warning: number;
  failed: number;
  skipped: number;
  notBuilt: number;
  total: number;
}

export interface SystemTestRun {
  runId: string;
  variant: SystemTestVariant;
  status: RunStatus;
  startedBy: string;
  env: string;
  createdAt: string;
  startedAt?: string;
  finishedAt?: string;
  durationMs?: number;
  steps: StepReport[];
  summary: RunSummary;
  /** The first failing step, highlighted on the page. */
  firstFailureStepId?: string;
  /** Documents the run created, so "Wipe test data" can remove exactly them. */
  createdPaths: string[];
}

export function summarise(steps: readonly StepReport[]): RunSummary {
  const s: RunSummary = {
    passed: 0,
    warning: 0,
    failed: 0,
    skipped: 0,
    notBuilt: 0,
    total: steps.length,
  };
  for (const step of steps) {
    if (step.status === 'passed') s.passed++;
    else if (step.status === 'warning') s.warning++;
    else if (step.status === 'failed') s.failed++;
    else if (step.status === 'skipped') s.skipped++;
    else if (step.status === 'not_built') s.notBuilt++;
  }
  return s;
}
